import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatINR, formatDate } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { Award, FileText, Wallet, Handshake, ScrollText, Bell, ArrowRight, ShieldCheck, TrendingUp, ClipboardList } from "lucide-react";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";
import { useLang } from "@/contexts/LanguageContext";

const bandStyle = {
  "Bank Ready": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Conditionally Ready": "bg-amber-100 text-amber-800 border-amber-200",
  "Preparation Required": "bg-orange-100 text-orange-800 border-orange-200",
  "Insufficient Information": "bg-slate-100 text-slate-800 border-slate-200",
};

// Explicit colour map — dynamic `bg-${color}-500` strings would be stripped by
// Tailwind's JIT purge, so keep the full class literals here for the purger to see.
const BG_MAP = {
  slate: "bg-slate-900",
  orange: "bg-orange-500",
  emerald: "bg-emerald-500",
};

function StatCard({ icon: Icon, title, value, sub, to, testId, color = "slate" }) {
  return (
    <Link
      to={to || "#"} data-testid={testId}
      className="p-6 rounded-lg border border-slate-200 bg-white card-hover flex items-start gap-4"
    >
      <div className={`w-10 h-10 rounded-md grid place-items-center text-white shrink-0 ${BG_MAP[color] || BG_MAP.slate}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs uppercase tracking-widest text-slate-500">{title}</div>
        <div className="mt-1 text-2xl font-bold text-slate-900 brand-heading truncate">{value}</div>
        {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
      </div>
      <ArrowRight className="w-4 h-4 text-slate-400 mt-1" />
    </Link>
  );
}

export default function BorrowerDashboard() {
  const [summary, setSummary] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [apps, setApps] = useState([]);
  const [interests, setInterests] = useState([]);
  const { t, tv } = useLang();
  useEffect(() => {
    api.get("/reports/dashboard-summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
    api.get("/applications").then((r) => setApps(r.data)).catch(() => {});
    api.get("/borrower/lender-interests").then((r) => setInterests(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="borrower-dashboard">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 brand-heading">{t("b_welcome")}</h1>
        <p className="text-slate-600 mt-1 text-sm sm:text-base">{t("b_welcome_sub")}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="p-6 rounded-lg border border-slate-200 bg-white">
          <div className="text-xs uppercase tracking-widest text-slate-500">{t("b_profile")}</div>
          <div className="mt-2 flex items-baseline gap-2">
            <div className="text-3xl font-bold text-slate-900 brand-heading" data-testid="profile-completion-value">{summary?.profile_completion || 0}%</div>
          </div>
          <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-orange-500 transition-all" style={{ width: `${summary?.profile_completion || 0}%` }} />
          </div>
          <Link to="/borrower/passport" className="mt-3 text-xs text-slate-900 font-semibold inline-flex items-center gap-1 hover:text-orange-600" data-testid="complete-profile-btn">{t("b_complete")} <ArrowRight className="w-3 h-3" /></Link>
        </div>

        <div className="p-6 rounded-lg border border-slate-200 bg-white">
          <div className="text-xs uppercase tracking-widest text-slate-500">{t("b_readiness")}</div>
          <div className={`mt-3 inline-flex px-3 py-1.5 rounded-full text-xs font-semibold border ${bandStyle[summary?.readiness_band] || "bg-slate-100 text-slate-700 border-slate-200"}`} data-testid="readiness-band">
            {summary?.readiness_band ? tv(summary.readiness_band) : t("b_not_assessed")}
          </div>
          <div className="mt-2 text-2xl font-bold text-slate-900 brand-heading" data-testid="readiness-score">{summary?.readiness_score || 0}<span className="text-sm text-slate-500 font-normal">/100</span></div>
          <Link to="/borrower/readiness" className="mt-2 text-xs text-slate-900 font-semibold inline-flex items-center gap-1 hover:text-orange-600" data-testid="view-readiness-btn">{t("b_view_report")} <ArrowRight className="w-3 h-3" /></Link>
        </div>

        <StatCard icon={Handshake} title={t("b_interests")} value={summary?.lender_interests || 0} sub={t("b_interests_sub")} to="/borrower/interests" testId="lender-interests-card" color="orange" />
        <StatCard icon={ClipboardList} title={t("b_apps")} value={summary?.applications || 0} sub={t("b_apps_sub")} to="/borrower/applications" testId="applications-card" color="emerald" />
      </div>

      {/* Second row — active funding, actions pending, documents, schemes (per product spec) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="dashboard-priority-row">
        <Link to="/borrower/funding" data-testid="active-funding-card" className="p-6 rounded-lg border border-slate-200 bg-white card-hover flex flex-col">
          <div className="text-xs uppercase tracking-widest text-slate-500">{t("b_funding")}</div>
          <div className="mt-2 text-2xl font-bold text-slate-900 brand-heading">
            {summary?.active_funding_amount ? `₹${(summary.active_funding_amount / 100000).toFixed(1)} L` : (summary?.active_funding_count ? `${summary.active_funding_count}` : "—")}
          </div>
          <div className="text-xs text-slate-500 mt-1">{summary?.active_funding_purpose ? tv(summary.active_funding_purpose) : t("b_add_funding")}</div>
          <div className="mt-auto pt-3 text-xs text-slate-900 font-semibold inline-flex items-center gap-1 hover:text-orange-600">{t("b_manage")} <ArrowRight className="w-3 h-3" /></div>
        </Link>

        <Link to="/borrower/readiness" data-testid="actions-pending-card" className="p-6 rounded-lg border border-slate-200 bg-white card-hover flex flex-col">
          <div className="text-xs uppercase tracking-widest text-slate-500">{t("b_actions")}</div>
          <div className="mt-2 text-2xl font-bold text-slate-900 brand-heading">{summary?.pending_actions ?? "—"}</div>
          <div className="text-xs text-slate-500 mt-1">{t("b_actions_sub")}</div>
          <div className="mt-auto pt-3 text-xs text-slate-900 font-semibold inline-flex items-center gap-1 hover:text-orange-600">{t("b_see_gaps")} <ArrowRight className="w-3 h-3" /></div>
        </Link>

        <Link to="/borrower/documents" data-testid="docs-status-card" className="p-6 rounded-lg border border-slate-200 bg-white card-hover flex flex-col">
          <div className="text-xs uppercase tracking-widest text-slate-500">{t("b_docs")}</div>
          <div className="mt-2 text-2xl font-bold text-slate-900 brand-heading">{summary?.documents_uploaded ?? "—"}</div>
          <div className="text-xs text-slate-500 mt-1">
            {summary?.documents_expiring ? `${summary.documents_expiring} expiring soon` : t("b_docs_sub")}
          </div>
          <div className="mt-auto pt-3 text-xs text-slate-900 font-semibold inline-flex items-center gap-1 hover:text-orange-600">{t("b_manage_docs")} <ArrowRight className="w-3 h-3" /></div>
        </Link>

        <Link to="/borrower/schemes" data-testid="relevant-schemes-card" className="p-6 rounded-lg border border-slate-200 bg-white card-hover flex flex-col">
          <div className="text-xs uppercase tracking-widest text-slate-500">{t("b_schemes")}</div>
          <div className="mt-2 text-2xl font-bold text-slate-900 brand-heading">{summary?.relevant_schemes ?? "—"}</div>
          <div className="text-xs text-slate-500 mt-1">{t("b_schemes_sub")}</div>
          <div className="mt-auto pt-3 text-xs text-slate-900 font-semibold inline-flex items-center gap-1 hover:text-orange-600">{t("b_explore")} <ArrowRight className="w-3 h-3" /></div>
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 p-6 rounded-lg border border-slate-200 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-slate-900 brand-heading">{t("b_quick")}</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { to: "/borrower/passport", icon: FileText, label: t("b_complete"), test: "qa-passport" },
              { to: "/borrower/funding", icon: Wallet, label: t("qa_funding"), test: "qa-funding" },
              { to: "/borrower/routes", icon: Award, label: t("qa_routes"), test: "qa-routes" },
              { to: "/borrower/schemes", icon: ScrollText, label: t("qa_schemes"), test: "qa-schemes" },
              { to: "/borrower/compare", icon: Handshake, label: t("qa_compare"), test: "qa-compare" },
              { to: "/borrower/consent", icon: ShieldCheck, label: t("qa_consent"), test: "qa-consent" },
            ].map((a) => {
              const Icon = a.icon;
              return (
                <Link key={a.test} to={a.to} data-testid={a.test} className="p-4 rounded-md border border-slate-200 hover:border-slate-400 bg-slate-50 hover:bg-white card-hover">
                  <Icon className="w-5 h-5 text-slate-700" />
                  <div className="mt-2 text-sm font-medium text-slate-900">{a.label}</div>
                </Link>
              );
            })}
          </div>
        </div>

        <div className="p-6 rounded-lg border border-slate-200 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-slate-900 brand-heading">{t("b_notifs")}</h2>
            <Bell className="w-4 h-4 text-slate-400" />
          </div>
          <ul className="space-y-3">
            {notifs.slice(0, 6).map((n) => (
              <li key={n.id} data-testid={`notif-${n.id}`} className="text-sm">
                <div className="font-medium text-slate-900">{tv(n.title)}</div>
                <div className="text-xs text-slate-500">{tv(n.message)}</div>
              </li>
            ))}
            {notifs.length === 0 && <li className="text-sm text-slate-500">{t("b_no_notifs")}</li>}
          </ul>
        </div>
      </div>

      <div className="p-6 rounded-lg border border-slate-200 bg-white">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-slate-900 brand-heading">{t("b_recent")}</h2>
          <Link to="/borrower/applications" className="text-xs font-semibold text-slate-900 hover:text-orange-600">{t("view_all")}</Link>
        </div>
        <div className="overflow-x-auto table-scroll -mx-6 px-6 sm:mx-0 sm:px-0">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200">
              <tr>
                <th className="py-2 pr-4">{t("col_lender")}</th>
                <th className="py-2 pr-4">{t("col_business")}</th>
                <th className="py-2 pr-4">{t("col_status")}</th>
                <th className="py-2 pr-4">{t("col_updated")}</th>
              </tr>
            </thead>
            <tbody>
              {apps.slice(0, 5).map((a) => (
                <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-3 pr-4 font-medium text-slate-900">{a.lender_name || "—"}</td>
                  <td className="py-3 pr-4">{a.business_name || "—"}</td>
                  <td className="py-3 pr-4"><StatusPill status={a.status} /></td>
                  <td className="py-3 pr-4 text-slate-500">{formatDate(a.updated_at)}</td>
                </tr>
              ))}
              {apps.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-slate-500">{t("no_apps")}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <DisclaimerBanner compact />
    </div>
  );
}
